// La page de santé : des signaux réels, lus à l'instant.
//
// Chaque bloc répond à une question qu'on se pose trop tard — la base est-elle
// pleine, la sauvegarde de cette nuit a-t-elle eu lieu, les tâches tournent-
// elles, les boîtes relèvent-elles encore, qu'est-ce qui a planté ? Ce qui ne
// se mesure pas n'est pas affiché : un tiret vaut mieux qu'un chiffre inventé.
import React, { useCallback, useEffect, useState } from 'react';
import { Database, HardDrive, Clock, Mail, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import api from '../../api/client';

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

const depuis = (v) => {
  if (!v) return '—';
  const ms = Date.now() - new Date(v).getTime();
  if (!Number.isFinite(ms)) return '—';
  const min = Math.round(ms / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `il y a ${h} h${min % 60 ? ` ${min % 60}` : ''}`;
  return `il y a ${Math.floor(h / 24)} jours`;
};

const dureeMs = (ms) => (ms == null ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);

// Les identifiants techniques des tâches, dits en français.
const NOMS_TACHES = {
  invoice_reminders: 'Relances de factures',
  questionnaire_positioning_j_minus_1: 'Questionnaire de positionnement (J-1)',
  questionnaire_satisfaction_j_plus_1: 'Questionnaire de satisfaction (J+1)',
  scheduled_emails_dispatcher: 'E-mails programmés',
  calendar_reminders: "Rappels d'agenda",
  google_pull: 'Relevé des agendas Google',
  mailbox_notifier: 'Nouveaux messages (boîtes)',
  microsoft_calendar_pull: 'Relevé des agendas Microsoft',
  microsoft_push_renew: 'Abonnements Microsoft (renouvellement)',
  demo_reminders: 'Relances des essais',
  emargement_preparation: "Feuilles d'émargement à venir",
  snooze_wake: 'Réveil des messages reportés',
};

function Carte({ icon: Icon, label, value, sub, tone = 'muted', testid }) {
  const tones = {
    ok: 'bg-[#E6FBF7] text-[#0F766E] border-[#99F6E4]',
    warn: 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]',
    bad: 'bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]',
    muted: 'bg-white text-[#4A4B63] border-[#EFF0F6]',
  };
  return (
    <div className={`rounded-xl border-2 p-3 ${tones[tone] || tones.muted}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{label}</span>
      </div>
      <div className="text-[22px] font-bold tabular-nums leading-tight">{value}</div>
      {sub && <div className="text-[11px] opacity-80 mt-0.5">{sub}</div>}
    </div>
  );
}

function Bloc({ titre, children, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#EFF0F6] p-5" data-testid={testid}>
      <h3 className="text-[14px] font-bold text-[#1A1B2E] mb-3">{titre}</h3>
      {children}
    </div>
  );
}

const th = 'text-left px-3 py-2 text-[11px] font-semibold text-[#6B6C85] uppercase tracking-wide';
const td = 'px-3 py-2 text-[12.5px] text-[#4A4B63] align-top';

export default function Sante() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await api.get('/superadmin/sante');
      setData(r.data);
      setErreur(null);
    } catch (e) {
      setErreur(e?.response?.data?.detail || 'La page de santé ne répond pas.');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  if (erreur) return <div className="rounded-xl bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] px-4 py-3 text-[13px]">{erreur}</div>;
  if (!data) return <div className="text-[13px] text-[#6B6C85] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lecture des signaux…</div>;

  const { version, base, sauvegardes, taches, boites, erreurs } = data;
  const baseTone = base.occupe_pct == null ? 'muted' : base.occupe_pct > 80 ? 'bad' : base.occupe_pct > 60 ? 'warn' : 'ok';
  const svgTone = !sauvegardes.accessible ? 'muted' : sauvegardes.en_retard ? 'bad' : 'ok';
  const tachesTone = !taches.actif ? 'bad' : taches.en_echec ? 'warn' : 'ok';
  const errTone = !erreurs.disponible ? 'muted' : erreurs.sur_24h > 0 ? 'warn' : 'ok';
  const boitesMuettes = boites.filter((b) => b.active && b.dernier_releve_min != null && b.dernier_releve_min > 30).length;

  return (
    <div className="space-y-4" data-testid="sante">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-[#6B6C85]">
          Version <code className="bg-[#EEF2FF] px-1 rounded text-[#4F46E5]">{version.commit || '?'}</code>
          {version.branche && <> · {version.branche}</>}
          {' · '}démarrée {depuis(version.demarre_le)}
          {taches.bac_a_sable && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-[10.5px] font-semibold">bac à sable</span>}
        </div>
        <button onClick={charger} disabled={chargement} className="h-8 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12px] font-semibold text-[#4A4B63] hover:bg-[#EEF2FF] flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${chargement ? 'animate-spin' : ''}`} /> Relire
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Carte icon={Database} label="Base de données" testid="sante-base" tone={baseTone}
          value={base.occupe_pct != null ? `${base.occupe_pct} %` : `${base.donnees_mo} Mo`}
          sub={`${base.occupe_mo} Mo sur ${base.quota_mo} Mo · ${base.documents.toLocaleString('fr-FR')} documents`} />
        <Carte icon={HardDrive} label="Sauvegarde" testid="sante-sauvegardes" tone={svgTone}
          value={!sauvegardes.accessible ? '—' : sauvegardes.derniere ? depuis(sauvegardes.derniere.quand) : 'aucune'}
          sub={!sauvegardes.accessible ? `dossier ${sauvegardes.dossier} inaccessible` : sauvegardes.derniere ? `${sauvegardes.derniere.taille_mo} Mo · ${sauvegardes.nombre} gardées, ${sauvegardes.retention_jours} jours` : 'aucun fichier trouvé'} />
        <Carte icon={Clock} label="Tâches planifiées" testid="sante-taches" tone={tachesTone}
          value={taches.actif ? `${taches.taches.length}` : 'arrêtées'}
          sub={taches.actif ? (taches.en_echec ? `${taches.en_echec} en échec` : 'toutes en ordre') : 'aucun processus ne tient le planificateur'} />
        <Carte icon={AlertTriangle} label="Erreurs sur 24 h" testid="sante-erreurs" tone={errTone}
          value={erreurs.disponible ? erreurs.sur_24h : '—'}
          sub={boitesMuettes ? `${boitesMuettes} boîte(s) sans relevé depuis 30 min` : `${boites.length} boîte(s) connectée(s)`} />
      </div>

      <Bloc titre="Tâches planifiées" testid="sante-taches-liste">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <th className={th}>Tâche</th><th className={th}>Planification</th><th className={th}>Dernière exécution</th><th className={th}>Durée</th><th className={th}>Prochaine</th><th className={th}>État</th>
            </tr></thead>
            <tbody>
              {taches.taches.length === 0 && <tr><td colSpan={6} className={`${td} italic text-[#8E8FA5]`}>Aucune tâche enregistrée — le planificateur n'a pas encore démarré dans ce déploiement.</td></tr>}
              {taches.taches.map((t) => (
                <tr key={t.tache} className="border-t border-[#F3F4F8]" data-testid={`sante-tache-${t.tache}`}>
                  <td className={td}><div className="text-[#1A1B2E] font-medium">{NOMS_TACHES[t.tache] || t.tache}</div><div className="text-[10.5px] text-[#8E8FA5] font-mono">{t.tache}</div></td>
                  <td className={`${td} font-mono text-[11px]`}>{t.planification || '—'}</td>
                  <td className={td} title={fmtDate(t.derniere_fin)}>{t.derniere_fin ? depuis(t.derniere_fin) : 'jamais'}</td>
                  <td className={td}>{dureeMs(t.derniere_duree_ms)}</td>
                  <td className={td}>{fmtDate(t.prochaine)}</td>
                  <td className={td}>
                    {t.ok === true && <span className="text-[#0F766E] font-semibold">OK</span>}
                    {t.ok === false && <span className="text-[#B91C1C] font-semibold" title={t.erreur || ''}>échec — {t.erreur}</span>}
                    {t.ok == null && <span className="text-[#8E8FA5]">pas encore tournée</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Bloc>

      <Bloc titre="Boîtes et agendas connectés" testid="sante-boites">
        {boites.length === 0 ? <div className="text-[12.5px] text-[#8E8FA5] italic">Aucune boîte connectée.</div> : (
          <table className="w-full">
            <thead><tr><th className={th}>Adresse</th><th className={th}>Espace</th><th className={th}>Fournisseur</th><th className={th}>Dernier relevé</th><th className={th}>État</th></tr></thead>
            <tbody>
              {boites.map((b, i) => (
                <tr key={i} className="border-t border-[#F3F4F8]">
                  <td className={`${td} text-[#1A1B2E]`}>{b.adresse}</td>
                  <td className={td}>{b.espace}{b.espace_existe === false && <span className="ml-1 text-[#B91C1C] font-semibold">(espace inexistant)</span>}</td>
                  <td className={td}>{b.fournisseur}{b.agenda ? ' · agenda' : ''}{b.courrier ? ' · courrier' : ''}</td>
                  <td className={td} title={fmtDate(b.dernier_releve)}>{b.dernier_releve ? depuis(b.dernier_releve) : 'jamais'}</td>
                  <td className={td}>{!b.active ? <span className="text-[#8E8FA5]">désactivée</span> : b.dernier_releve_min != null && b.dernier_releve_min > 30 ? <span className="text-[#B91C1C] font-semibold">muette</span> : <span className="text-[#0F766E] font-semibold">OK</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Bloc>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Bloc titre="Ce qui pèse dans la base" testid="sante-collections">
          <table className="w-full">
            <thead><tr><th className={th}>Collection</th><th className={`${th} text-right`}>Documents</th><th className={`${th} text-right`}>Mo</th></tr></thead>
            <tbody>
              {base.collections.map((c) => (
                <tr key={c.nom} className="border-t border-[#F3F4F8]">
                  <td className={`${td} font-mono text-[11.5px]`}>{c.nom}</td>
                  <td className={`${td} text-right tabular-nums`}>{c.documents.toLocaleString('fr-FR')}</td>
                  <td className={`${td} text-right tabular-nums`}>{c.donnees_mo}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {base.autres_bases.length > 0 && (
            <div className="mt-3 text-[12px] text-[#4A4B63]">
              <div className="font-semibold text-[#1A1B2E] mb-1">Autres bases sur le même cluster — elles comptent dans le quota</div>
              {base.autres_bases.map((a) => (
                <div key={a.nom} className="flex justify-between border-t border-[#F3F4F8] py-1">
                  <span className="font-mono text-[11.5px]">{a.nom}</span>
                  <span className="tabular-nums">{a.erreur ? <span className="text-[#8E8FA5]">{a.erreur}</span> : `${a.donnees_mo} Mo`}</span>
                </div>
              ))}
            </div>
          )}
          {base.autres_bases_erreur && <div className="mt-2 text-[11.5px] text-[#8E8FA5]">Autres bases non listées : {base.autres_bases_erreur}</div>}
        </Bloc>

        <Bloc titre="Dernières erreurs" testid="sante-erreurs-liste">
          {!erreurs.disponible && <div className="text-[12.5px] text-[#8E8FA5] italic">Le journal des erreurs n'est pas disponible.</div>}
          {erreurs.disponible && erreurs.dernieres.length === 0 && <div className="text-[12.5px] text-[#0F766E]">Aucune erreur enregistrée.</div>}
          {erreurs.dernieres.map((e, i) => (
            <div key={i} className="border-t border-[#F3F4F8] py-2 first:border-t-0">
              <div className="text-[11px] text-[#8E8FA5]">{fmtDate(e.quand)} · {e.source}</div>
              <div className="text-[12.5px] text-[#1A1B2E] break-words">{e.message}</div>
              {e.trace && <details className="mt-1"><summary className="text-[11px] text-[#6B6C85] cursor-pointer">trace</summary><pre className="text-[10.5px] text-[#4A4B63] whitespace-pre-wrap max-h-48 overflow-auto bg-[#F6F7FB] rounded p-2 mt-1">{e.trace}</pre></details>}
            </div>
          ))}
        </Bloc>
      </div>

      {sauvegardes.accessible && sauvegardes.fichiers.length > 0 && (
        <Bloc titre="Sauvegardes présentes sur le serveur" testid="sante-sauvegardes-liste">
          <table className="w-full">
            <thead><tr><th className={th}>Fichier</th><th className={th}>Date</th><th className={`${th} text-right`}>Taille</th></tr></thead>
            <tbody>
              {sauvegardes.fichiers.map((f) => (
                <tr key={f.nom} className="border-t border-[#F3F4F8]">
                  <td className={`${td} font-mono text-[11.5px]`}>{f.nom}</td>
                  <td className={td}>{fmtDate(f.quand)}</td>
                  <td className={`${td} text-right tabular-nums`}>{f.taille_mo} Mo</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[11.5px] text-[#8E8FA5] mt-2">Dossier {sauvegardes.dossier}, monté en lecture seule : l'application ne peut ni écrire ni effacer une sauvegarde.</div>
        </Bloc>
      )}
    </div>
  );
}
